import { createClient, createRequest } from 'urql';
import { pipe, subscribe } from 'wonka';
import { globalRequestQueue } from '../utils/RequestQueue';
import { globalRequestCache, RequestCache } from '../utils/RequestCache';

export default class Model {
    // Identifies this build as a DST app. Validated by the dstAppSyncAuthorizer lambda
    // against its APP_KEY env var; a request without it is rejected outright. This ships in
    // the JS bundle and is therefore public - it is a filter, not authentication. The user's
    // signed token below is what actually proves who is calling.
    static APP_KEY = "dstapp_3Kl2AYL8FN5_wTrWN-zbJrQ0vSV9jx_hmQ__oMf7hQ4";

    static urqlClient = createClient({
        url: 'https://ewh7pcy2zjh6zcc2b3zsdpsuze.appsync-api.us-west-2.amazonaws.com/graphql',
        // url: 'https://7ymw6tbkmbds5buzwbmj6odley.appsync-api.us-west-2.amazonaws.com/graphql',

        // A function, not an object: it is evaluated per request, so the token picked up
        // here is the current one. As a static object this was frozen at module load and
        // would never see a token acquired after the page booted.
        fetchOptions: () => {
            const token = localStorage["dst-login-token"];
            // AuthContext.signout writes the string "undefined" rather than removing the
            // key, so a truthiness check alone is not enough.
            const signedIn = token && token !== "undefined" && token !== "null";
            return {
                headers: {
                    "Content-Type": "application/graphql",
                    "x-dst-app-key": Model.APP_KEY,
                    // Logged out, only the pre-login fields (login / isEmailValid /
                    // sendPasswordReset) are reachable; the authorizer denies the rest.
                    "Authorization": signedIn ? token : "guest",
                    'Access-Control-Allow-Origin': '*'
                }
            };
        },
    });;

    static runQueryCachedFirst = (query, vars) => {
        return new Promise((resolve, reject) => {
            const request = createRequest(query, vars);
            pipe(this.urqlClient.executeQuery(request, {requestPolicy: 'cache-first'}), subscribe(res => {
                if (this.isResponseAnError(res)){
                    reject(res);
                }
                resolve(res);
            }));
        });
    }

    static runMutation = async(query, vars) => {
        // Wrap mutations in the request queue as well
        return globalRequestQueue.add(async () => {
            return new Promise((resolve, reject) => {
                const request = createRequest(query, vars);
                pipe(this.urqlClient.executeMutation(request), subscribe(res => {
                    if (this.isResponseAnError(res)){
                        reject(res);
                    }
                    resolve(res);
                }));
            });
        }, `mutation_${Date.now()}`);
    }

    static runQueryNetworkOnly = (query, vars, abortSignal, searchId) => {
        // Wrap the entire query execution in the request queue
        return globalRequestQueue.add(async () => {
            return new Promise((resolve, reject) => {
                const request = createRequest(query, vars);
                
                // Provide fallback values for backward compatibility
                searchId = searchId || `query_${Date.now()}`;
                
                // Check if request was cancelled before starting
                if (abortSignal?.aborted) {
                    // console.log(`🚫 GraphQL Query [${searchId}] aborted before execution`);
                    reject(new DOMException('Request was cancelled', 'AbortError'));
                    return;
                }
                
                // Set up abort listener
                let subscription;
                const abortHandler = () => {
                    // console.log(`🛑 GraphQL Query [${searchId}] aborted during execution`);
                    if (subscription) {
                        subscription.unsubscribe();
                    }
                    reject(new DOMException('Request was cancelled', 'AbortError'));
                };
                
                if (abortSignal) {
                    abortSignal.addEventListener('abort', abortHandler);
                }
                
                // Execute the GraphQL query
                subscription = pipe(
                    this.urqlClient.executeQuery(request, {requestPolicy: 'network-only'}), 
                    subscribe(res => {
                        // Remove abort listener on completion
                        if (abortSignal) {
                            abortSignal.removeEventListener('abort', abortHandler);
                        }
                        
                        if (this.isResponseAnError(res)){
                            reject(res);
                        } else {
                            if (searchId !== `query_${Date.now()}`) {
                                // console.log(`✅ GraphQL Query [${searchId}] completed successfully`);
                            }
                            resolve(res);
                        }
                    })
                );
            });
        }, searchId);
    }

    static runQuery = (query, vars) => {
        return this.runQueryNetworkOnly(query, vars);
    }

    static runQueryCacheAndNetwork = (query, vars, abortSignal, searchId) => {
        return new Promise((resolve, reject) => {
            const request = createRequest(query, vars);
            
            // Provide fallback values for backward compatibility
            searchId = searchId || `cache_query_${Date.now()}`;
            
            // Check if request was cancelled before starting
            if (abortSignal?.aborted) {
                console.log(`🚫 GraphQL CacheAndNetwork Query [${searchId}] aborted before execution`);
                reject(new DOMException('Request was cancelled', 'AbortError'));
                return;
            }
            
            // Set up abort listener
            let subscription;
            const abortHandler = () => {
                console.log(`🛑 GraphQL CacheAndNetwork Query [${searchId}] aborted during execution`);
                if (subscription) {
                    subscription.unsubscribe();
                }
                reject(new DOMException('Request was cancelled', 'AbortError'));
            };
            
            if (abortSignal) {
                abortSignal.addEventListener('abort', abortHandler);
            }
            
            // Execute the GraphQL query
            subscription = pipe(
                this.urqlClient.executeQuery(request, {requestPolicy: 'cache-and-network'}), 
                subscribe(res => {
                    // Remove abort listener on completion
                    if (abortSignal) {
                        abortSignal.removeEventListener('abort', abortHandler);
                    }
                    
                    if (this.isResponseAnError(res)){
                        reject(res);
                    } else {
                        if (searchId !== `cache_query_${Date.now()}`) {
                            // console.log(`✅ GraphQL CacheAndNetwork Query [${searchId}] completed successfully`);
                        }
                        resolve(res);
                    }
                })
            );
        });
    }

    static searchBy = async (graphQLQuery, key="uuid", value, query="", limit=15) => {
        const vars = {
            input: {
                query, 
                limit,
                fieldName: key,
                filters: value === null ? [] :
                    [{ 
                        id: value,
                        fieldName: key
                    }]
            }
        }
        const res = await this.runQueryNetworkOnly(graphQLQuery, vars);
        // console.log(`@@@@@@@@@@@@@@@@@@@@@@@@@ Model.searchBy.runQuery [COMPLETE]: ${JSON.stringify(res)}`);
        return res.data;
    }

    static filterBy = async (graphQLQuery, searchList=[], sortField="created_at", sortOrder="desc", first, after, last, before, ignoreCache, additionalVars={}, abortSignal, searchId) => {
        const vars = {
            input: {
                searchList,
                sortOrder,
                sortField,
                first,
                last,
                after,
                before
            },
            ...(additionalVars || {})
        }
        //console.log("vars", vars)
        if (ignoreCache){
            const res = await this.runQueryNetworkOnly(graphQLQuery, vars, abortSignal, searchId);
            //console.log('res1', res)
            return res.data;
        }else{
            const res = await this.runQueryCacheAndNetwork(graphQLQuery, vars, abortSignal, searchId);
            //console.log('res2', res)
            return res.data;
        }
    }

    

    static isResponseAnError = (res) => {
        return res && !res.data && res.error !== undefined;
    }
}